import React, { useCallback, useEffect, useState } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { TextField, FormHelperText } from '@material-ui/core';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import debounce from 'lodash/debounce';

/**
 * UniqueComponentNameInput - 带唯一性校验的 Component 名称输入框
 *
 * 功能：
 * - 实时检查 Component 名称是否已存在于 Catalog
 * - 校验名称格式（小写字母、数字、连字符）
 * - 防抖处理，避免频繁请求
 *
 * 用法：
 * ```yaml
 * componentName:
 *   title: Component 名称
 *   type: string
 *   ui:field: UniqueComponentNameInput
 * ```
 */
export const UniqueComponentNameInput = (
  props: FieldExtensionComponentProps<string>,
) => {
  const {
    onChange,
    rawErrors,
    required,
    formData,
    uiSchema,
    idSchema,
    schema,
  } = props;

  const configApi = useApi(configApiRef);
  const backendUrl = configApi.getString('backend.baseUrl');

  const [value, setValue] = useState(formData || '');
  const [checking, setChecking] = useState(false);
  const [existsError, setExistsError] = useState<string | null>(null);
  const [formatError, setFormatError] = useState<string | null>(null);

  // 格式校验：小写字母开头，只能包含小写字母、数字、连字符
  const validateFormat = (name: string): string | null => {
    if (!name) return null;
    if (!/^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/.test(name)) {
      return '名称必须以小写字母或数字开头，只能包含小写字母、数字和连字符';
    }
    if (name.length < 2) {
      return '名称至少需要 2 个字符';
    }
    if (name.length > 63) {
      return '名称不能超过 63 个字符';
    }
    return null;
  };

  // 检查 Component 是否已存在
  const checkComponentExists = useCallback(
    async (name: string): Promise<boolean> => {
      if (!name || name.length < 2) return false;

      try {
        const response = await fetch(
          `${backendUrl}/api/catalog/entities/by-name/component/default/${name}`,
        );
        return response.ok; // 200 表示存在
      } catch {
        // 网络错误时不阻止创建
        return false;
      }
    },
    [backendUrl],
  );

  // 防抖检查
  const debouncedCheck = useCallback(
    debounce((name: string) => {
      if (!name || validateFormat(name)) {
        setExistsError(null);
        setChecking(false);
        return;
      }

      setChecking(true);
      checkComponentExists(name).then((exists) => {
        setChecking(false);
        if (exists) {
          setExistsError(`Component "${name}" 已存在，请使用其他名称`);
        } else {
          setExistsError(null);
        }
      });
    }, 500),
    [checkComponentExists],
  );

  // 处理输入变化
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value.toLowerCase().trim();
    setValue(newValue);

    // 格式校验
    const fmtError = validateFormat(newValue);
    setFormatError(fmtError);

    // 如果格式正确，检查是否存在
    if (!fmtError && newValue) {
      setChecking(true);
      debouncedCheck(newValue);
    } else {
      setExistsError(null);
    }

    // 只有格式正确且不存在时才更新表单值
    if (!fmtError) {
      onChange(newValue);
    } else {
      onChange(undefined); // 清空值，阻止提交
    }
  };

  // 当存在性检查完成后，更新表单值
  useEffect(() => {
    if (existsError) {
      onChange(undefined); // 存在时清空值，阻止提交
    } else if (value && !formatError) {
      onChange(value);
    }
  }, [existsError, value, formatError, onChange]);

  const hasError = !!(formatError || existsError || (rawErrors && rawErrors.length > 0));
  const helperText = formatError || existsError || (checking ? '检查中...' : '');

  return (
    <>
      <TextField
        id={idSchema.$id}
        label={schema.title || 'Component 名称'}
        value={value}
        onChange={handleChange}
        required={required}
        error={hasError}
        helperText={helperText}
        fullWidth
        variant="outlined"
        placeholder="例如: payment-service-test"
        InputProps={{
          autoComplete: 'off',
        }}
      />
      {uiSchema?.['ui:help'] && !hasError && (
        <FormHelperText>{uiSchema['ui:help']}</FormHelperText>
      )}
    </>
  );
};

